import { supabase } from '../supabase';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Marca por app (Impulza / Gastify). billingBrandConfig esta PROTECTED en el sync,
// asi que cada app conserva su brandName sin hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';

export const SETUP_ONBOARDING_STATUS_PENDING = 'pending';
export const SETUP_ONBOARDING_STATUS_COMPLETED = 'completed';

// Etapa "Empeza a operar" del plan de onboarding (kiosco / comercio).
// Es el puente entre "primera venta" y la operacion diaria: un checklist
// persistente que el dashboard muestra hasta que el negocio esta en marcha.
//
// Cada paso declara como detectarse a partir de senales reales (detect),
// si es obligatorio para considerar el setup terminado (required) y a donde
// lleva su CTA. La deteccion automatica se complementa con un estado manual
// por organizacion (pasos marcados o el panel descartado).
export const SETUP_ONBOARDING_STEPS = [
  // ── Obligatorios: lo minimo para estar operando (no agregan friccion antes
  //    de la primera venta; viven aca, en el panel persistente). ──
  {
    id: 'load-catalog',
    stage: 1,
    required: true,
    label: 'Cargá tu catálogo',
    description: 'Cargalos a mano, importá una planilla o escanealos con la cámara para venderlos al instante.',
    cta: { label: 'Cargar productos', route: '/sistema/producto-nuevo' },
    altCta: {
      label: 'Importar planilla',
      route: '/sistema/importaciones?tipo=PRODUCTOS',
      hint: 'Cargá muchos productos de una vez desde una planilla de Excel (.xlsx): nombre, costo, precio y stock.',
    },
    // Se marca apenas hay catalogo cargado; el contador refleja el avance real.
    detect: (s) => Number(s?.productsCount || 0) > 0,
    progress: (s) => Math.min(Number(s?.productsCount || 0), 10),
    progressTarget: 10,
  },
  {
    // Va entre el catalogo y la primera venta a proposito: un producto recien
    // creado nace con stock 0 y el POS no lo deja vender (allow_negative_stock
    // viene apagado en comercio y kiosco). Sin este paso, "Hace tu primera
    // venta" arranca contra una pared.
    id: 'load-stock',
    stage: 1,
    required: true,
    label: 'Ingresá el stock que tenés',
    description: 'Cargá las unidades que tenés hoy de cada producto: recién ahí podés venderlos y el stock se descuenta solo.',
    cta: { label: 'Ingresar stock', route: '/sistema/inventario-ajuste' },
    detect: (s) => Number(s?.stockedProductsCount || 0) > 0,
  },
  {
    id: 'first-sale',
    stage: 1,
    required: true,
    label: 'Hacé tu primera venta',
    description: 'Cobrá una operación desde la caja para ver el circuito completo de punta a punta.',
    cta: { label: 'Ir al punto de venta', route: '/sistema/ventas-pos' },
    detect: (s) => Number(s?.salesCount || 0) > 0,
  },
  {
    id: 'open-cash',
    stage: 1,
    required: true,
    label: 'Abrí y controlá tu caja',
    description: 'Registrá el efectivo del turno para cerrar el día con los números cuadrados.',
    cta: { label: 'Ir a caja', route: '/sistema/caja' },
    detect: (s) => Number(s?.cashMovementsCount || 0) > 0,
  },
  // ── Opcionales: ajustes de puesta a punto que el preset ya dejo cargados y
  //    el usuario solo confirma cuando quiere (no bloquean el panel). ──
  {
    // H3 — Proveedores y compras salieron del recorrido guiado inicial:
    // se aprenden aca, bajo demanda, con su guia propia.
    id: 'first-purchase',
    stage: 2,
    label: 'Registrá tu primera compra',
    description: 'Cargá un proveedor y su factura de compra para sumar stock y controlar tus costos.',
    cta: { label: 'Registrar compra', route: '/sistema/compras-nueva' },
    detect: (s) => Number(s?.purchasesCount || 0) > 0,
  },
  {
    id: 'business-data',
    stage: 1,
    label: 'Completá los datos de tu negocio',
    description: 'Sumá razón social, CUIT y condición frente al IVA para que aparezcan en tickets y comprobantes.',
    cta: { label: 'Datos de la empresa', route: '/sistema/empresa-datos' },
    detect: (s) => s?.hasFiscalProfile === true,
  },
  {
    id: 'load-suppliers',
    stage: 2,
    label: 'Cargá tus proveedores',
    description: 'Con el proveedor cargado, cada compra actualiza el costo y la cuenta corriente sola.',
    cta: { label: 'Agregar proveedor', route: '/sistema/proveedores' },
    detect: (s) => Number(s?.suppliersCount || 0) > 0,
  },
  {
    id: 'product-costs',
    stage: 2,
    label: 'Cargá el costo de tus productos',
    description: 'Sin costo no hay margen: es lo que necesita el módulo de rentabilidad para decirte cuánto ganás.',
    cta: { label: 'Ver productos', route: '/sistema/productos' },
    // Cargar UN producto con costo (lo que hace la guia de catalogo) no
    // significa tener el catalogo costeado: se pide cobertura real.
    detect: (s) => {
      const total = Number(s?.productsCount || 0);
      const conCosto = Number(s?.productsWithCostCount || 0);
      return total >= 5 && conCosto / total >= 0.8;
    },
    progress: (s) => Math.min(Number(s?.productsWithCostCount || 0), 10),
    progressTarget: 10,
  },
  {
    id: 'product-categories',
    stage: 2,
    label: 'Clasificá tus productos por categoría',
    description: 'Los reportes agrupan por categoría: sin productos clasificados, el análisis de ventas queda en una sola bolsa.',
    cta: { label: 'Ver productos', route: '/sistema/productos' },
    // Las categorias vienen sembradas por el preset, y clasificar un producto
    // suelto no es tener el catalogo clasificado: se pide cobertura real.
    detect: (s) => {
      const total = Number(s?.productsCount || 0);
      const clasificados = Number(s?.categorizedProductsCount || 0);
      return total >= 5 && clasificados / total >= 0.8;
    },
  },
  {
    id: 'invite-team',
    stage: 3,
    label: 'Sumá a tu equipo',
    description: 'Invitá a tus empleados y definí qué puede ver y hacer cada uno.',
    cta: { label: 'Agregar usuario', route: '/sistema/usuario-nuevo' },
    detect: (s) => Number(s?.usersCount || 0) > 1,
  },
  {
    id: 'load-clients',
    stage: 3,
    label: 'Cargá tus clientes',
    description: 'Con el cliente cargado podés vender en cuenta corriente y saber quién te debe.',
    cta: { label: 'Agregar cliente', route: '/sistema/clientes' },
    tourId: 'setup-client-credit',
    // El alta siembra "Consumidor Final": la senal es tener alguno propio.
    detect: (s) => Number(s?.clientsCount || 0) > 1,
  },
  {
    id: 'price-lists',
    stage: 3,
    label: 'Armá tus listas de precios',
    description: 'Precios distintos por canal o tipo de cliente (mayorista, delivery) sin tocar el producto.',
    cta: { label: 'Ver listas de precios', route: '/sistema/listas-precios' },
    tourId: 'configure-price-lists',
    // El alta deja la lista base; la senal es una segunda lista.
    detect: (s) => Number(s?.priceListsCount || 0) > 1,
  },
  {
    id: 'bank-accounts',
    stage: 3,
    label: 'Cargá tus cuentas bancarias',
    description: 'Necesario para conciliar transferencias y ver la plata que tenés disponible en el banco.',
    cta: { label: 'Ir a bancos', route: '/sistema/bancos' },
    detect: (s) => Number(s?.banksCount || 0) > 0,
  },
  {
    // El alta crea la fila de store_config (slug + titulo), asi que "existe la
    // tienda" no prueba nada. Lo que la pone a funcionar es el WhatsApp donde
    // caen los pedidos: sin eso el boton de la tienda no lleva a ningun lado.
    id: 'online-store',
    stage: 3,
    label: 'Publicá tu tienda online',
    description: 'Cargá el WhatsApp donde querés recibir los pedidos y vendé con el mismo catálogo y el mismo stock del mostrador.',
    cta: { label: 'Configurar tienda', route: '/sistema/tienda-config' },
    tourId: 'publish-web-store',
    detect: (s) => s?.storeContactReady === true || Number(s?.storeOrdersCount || 0) > 0,
  },
  {
    // Volvio al checklist, pero apuntando a lo unico que el preset NO deja
    // hecho: a que cuenta se acredita cada medio electronico. Eso alimenta
    // paymentSettlement y el flujo de fondos; la comision sembrada, no.
    id: 'payment-methods',
    stage: 3,
    label: 'Vinculá tus cobros con tu banco',
    description: 'Efectivo, transferencia y tarjetas ya vienen cargados. Decile a cada uno a qué cuenta se acredita y en cuántos días para que la proyección de caja sea real.',
    cta: { label: 'Configurar medios de cobro', route: '/sistema/metodos-cobro' },
    detect: (s) => Number(s?.linkedPaymentMethodsCount || 0) > 0,
  },
  {
    id: 'configure-billing',
    stage: 2,
    label: 'Configurá tu facturación ARCA',
    description: 'Cargá el certificado y el punto de venta: es lo que separa cobrar de facturar.',
    // F9 — Antes apuntaba a /sistema/empresa-datos; ahora lleva directo a la
    // pantalla de facturacion donde vive el AfipCertificateWizard.
    cta: { label: 'Configurar facturación ARCA', route: '/sistema/config-facturacion' },
    detect: (s) => Number(s?.afipPointsCount || 0) > 0,
  },
];

// F5 — Paso extra de gastronomia: primera receta. Se inserta despues del catalogo.
const RECIPE_STEP = {
  id: 'load-recipes',
  // Sin stage caia en la etapa 3 por defecto: la primera receta es puesta a
  // punto inicial de un negocio gastronomico, no crecimiento.
  stage: 1,
  required: true,
  label: 'Cargá tu primera receta',
  description: 'Definí los insumos y cantidades de un plato para calcular su costo real y su margen.',
  cta: { label: 'Crear receta', route: '/sistema/recetas' },
  detect: (s) => Number(s?.recipesCount || 0) > 0,
};

// Union de todos los pasos posibles (para validar ids y persistir estado sin
// importar el vertical con el que se construyo el checklist).
const STEP_LOOKUP = new Map([...SETUP_ONBOARDING_STEPS, RECIPE_STEP].map((step) => [step.id, step]));

/**
 * F5 — Pasos del checklist adaptados al rubro (business_type). Sin business_type
 * (o rubro desconocido) devuelve el set base de comercio, por lo que las orgs
 * existentes / no v2 ven exactamente lo de antes.
 *  - gastronomia: catalogo -> insumos, + "Cargá tu primera receta".
 *  - servicios: "Cargá tus 3 servicios más vendidos" (target 3, sin stock).
 *  - indumentaria: catalogo con variantes (talle/color).
 *  - comercio / default: base actual.
 */
export function getSetupOnboardingSteps(businessType) {
  const type = String(businessType || '').toLowerCase();

  if (type === 'gastronomia') {
    return SETUP_ONBOARDING_STEPS.flatMap((step) => {
      if (step.id !== 'load-catalog') return [step];
      return [
        {
          ...step,
          label: 'Cargá tus productos e insumos',
          description: 'Cargá lo que vendés y los insumos que después vas a usar en tus recetas.',
        },
        RECIPE_STEP,
      ];
    });
  }

  if (type === 'servicios') {
    return SETUP_ONBOARDING_STEPS.map((step) => {
      if (step.id !== 'load-catalog') return step;
      return {
        ...step,
        label: 'Cargá tus 3 servicios más vendidos',
        description: 'Cargá los servicios que ofrecés para poder presupuestarlos, facturarlos y cobrarlos.',
        cta: { label: 'Cargar servicios', route: '/sistema/producto-nuevo' },
        progress: (s) => Math.min(Number(s?.productsCount || 0), 3),
        progressTarget: 3,
      };
    });
  }

  if (type === 'indumentaria') {
    return SETUP_ONBOARDING_STEPS.map((step) => {
      if (step.id !== 'load-catalog') return step;
      return {
        ...step,
        label: 'Cargá tu catálogo con variantes',
        description: 'Cargá tus prendas con talle y color para venderlas eligiendo la variante en el POS.',
      };
    });
  }

  return SETUP_ONBOARDING_STEPS;
}

/**
 * Guia -> paso del checklist. Varias guias comparten pantalla con un paso, y
 * una (el importador de Excel) es un camino alternativo de otro: sin este mapa
 * no se puede dar por hecho el paso correcto al terminar un recorrido.
 */
const TOUR_TO_STEP = { 'load-catalog-excel': 'load-catalog' };

export function getSetupStepIdForTour(tourId) {
  if (!tourId) return null;
  if (TOUR_TO_STEP[tourId]) return TOUR_TO_STEP[tourId];
  if (STEP_LOOKUP.has(tourId)) return tourId;
  const match = [...SETUP_ONBOARDING_STEPS, RECIPE_STEP].find((step) => step.tourId === tourId);
  return match ? match.id : null;
}

export function getSetupOnboardingBrandName() {
  return BRAND_NAME;
}

// ── Estado persistido por organizacion (organizations.setup_onboarding_status) ──

export function normalizeSetupOnboardingState(value) {
  const base = { version: 1, dismissed: false, manual: {}, updatedAt: null };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return base;

  const manualEntries = value.manual && typeof value.manual === 'object' ? value.manual : {};
  const manual = Object.entries(manualEntries).reduce((acc, [stepId, status]) => {
    if (STEP_LOOKUP.has(stepId) && String(status).toLowerCase() === SETUP_ONBOARDING_STATUS_COMPLETED) {
      acc[stepId] = SETUP_ONBOARDING_STATUS_COMPLETED;
    }
    return acc;
  }, {});

  return {
    version: 1,
    dismissed: value.dismissed === true,
    manual,
    updatedAt: value.updatedAt || null,
  };
}

// ── Construccion del checklist (pura, testeable) ──

export function buildSetupOnboardingChecklist(signals = {}, state = null, businessType = null) {
  const normalizedState = normalizeSetupOnboardingState(state);

  return getSetupOnboardingSteps(businessType).map((step) => {
    const autoDone = typeof step.detect === 'function' ? Boolean(step.detect(signals)) : false;
    const manualDone = normalizedState.manual[step.id] === SETUP_ONBOARDING_STATUS_COMPLETED;
    const done = autoDone || manualDone;

    const current = typeof step.progress === 'function' ? Number(step.progress(signals) || 0) : null;

    return {
      id: step.id,
      // La etapa la declara el paso: la pantalla no tiene que adivinarla con
      // una lista de ids paralela (asi se colaron pasos inexistentes).
      stage: step.stage || 3,
      label: step.label,
      description: step.description,
      cta: step.cta,
      altCta: step.altCta || null,
      // Guia asociada: por defecto comparte id con el paso, salvo los que
      // reusan un tour existente con otro nombre.
      tourId: step.tourId || step.id,
      required: step.required !== false,
      optional: step.required === false,
      done,
      doneBy: done ? (autoDone ? 'auto' : 'manual') : null,
      progress: current,
      progressTarget: step.progressTarget || null,
    };
  });
}

export function getSetupOnboardingProgress(checklist = []) {
  const required = checklist.filter((step) => step.required);
  const requiredDone = required.filter((step) => step.done);
  const allDone = checklist.filter((step) => step.done);

  const total = required.length || checklist.length;
  const done = required.length ? requiredDone.length : allDone.length;

  return {
    total,
    done,
    pct: total > 0 ? Math.round((done / total) * 100) : 0,
    allRequiredDone: required.length > 0 ? requiredDone.length === required.length : allDone.length === checklist.length,
  };
}

// El panel se muestra mientras quede algo obligatorio pendiente y no fue descartado.
export function shouldShowSetupOnboarding(checklist = [], state = null) {
  const normalizedState = normalizeSetupOnboardingState(state);
  if (normalizedState.dismissed) return false;
  const { allRequiredDone } = getSetupOnboardingProgress(checklist);
  return !allRequiredDone;
}

// ── Senales reales desde Supabase ──

const safeCount = async (table, organizationId) => {
  try {
    const { count, error } = await supabase
      .from(table)
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId);
    if (error) return 0;
    return Number(count || 0);
  } catch {
    // Tabla inexistente o sin permisos: tratamos la senal como "sin datos".
    return 0;
  }
};

const isMissingTableError = (error) => {
  const haystack = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''} ${error?.code || ''}`.toLowerCase();
  return (
    haystack.includes('does not exist') ||
    haystack.includes('could not find the table') ||
    haystack.includes('schema cache') ||
    haystack.includes('42p01') || // undefined_table (Postgres)
    haystack.includes('pgrst205') // PostgREST: tabla no encontrada en el schema cache
  );
};

// La tabla fisica de usuarios varia por tenant: Impulza resuelve la org desde
// `profiles`; otros deploys usan `public.users`. Resolvemos una vez por sesion
// y cacheamos para no repetir 404 en consola en cada refresh del checklist.
let orgUsersTable = null;

const countOrgUsers = async (organizationId) => {
  const candidates = orgUsersTable ? [orgUsersTable] : ['profiles', 'users'];
  for (const table of candidates) {
    try {
      const { count, error } = await supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId);
      if (!error) {
        orgUsersTable = table;
        return Number(count || 0);
      }
      // Error real (RLS, red, etc.): senal "sin datos", igual que safeCount.
      if (!isMissingTableError(error)) return 0;
    } catch {
      return 0;
    }
  }
  return 0;
};

// Cuenta filas con un filtro extra. Misma tolerancia que safeCount: si la
// tabla o la columna no existen en este deploy, la senal vale 0.
const safeCountWhere = async (table, organizationId, apply) => {
  try {
    let query = supabase
      .from(table)
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId);
    query = apply(query);
    const { count, error } = await query;
    if (error) return 0;
    return Number(count || 0);
  } catch {
    return 0;
  }
};

// La tienda online se da por publicada cuando tiene un canal de contacto
// cargado: el alta siembra slug y titulo, pero nunca el WhatsApp.
const fetchStoreReadiness = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('store_config')
      .select('whatsapp, instagram, logo_url, banner_url')
      .eq('organization_id', organizationId)
      .maybeSingle();
    if (error || !data) return { storeContactReady: false };
    const filled = (value) => String(value || '').trim().length > 0;
    return {
      storeContactReady: filled(data.whatsapp) || filled(data.instagram) || filled(data.logo_url) || filled(data.banner_url),
    };
  } catch {
    return { storeContactReady: false };
  }
};

// Datos fiscales del negocio: lo que despues sale impreso en el comprobante.
const fetchOrgFiscalProfile = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('legal_name, cuit, tax_condition, address')
      .eq('id', organizationId)
      .maybeSingle();
    if (error || !data) return { hasFiscalProfile: false };
    const filled = (value) => String(value || '').trim().length > 0;
    return {
      hasFiscalProfile: filled(data.legal_name) && filled(data.cuit) && filled(data.tax_condition),
    };
  } catch {
    return { hasFiscalProfile: false };
  }
};

export async function fetchSetupOnboardingSignals(organizationId) {
  if (!organizationId) {
    return {
      productsCount: 0, salesCount: 0, cashMovementsCount: 0, usersCount: 0, recipesCount: 0, purchasesCount: 0,
      stockedProductsCount: 0,
      hasFiscalProfile: false, afipPointsCount: 0, suppliersCount: 0,
      categorizedProductsCount: 0, productsWithCostCount: 0,
      clientsCount: 0, priceListsCount: 0, banksCount: 0,
      storeContactReady: false, storeOrdersCount: 0, linkedPaymentMethodsCount: 0,
    };
  }

  const [
    productsCount,
    salesCount,
    cashMovementsCount,
    usersCount,
    recipesCount,
    purchasesCount,
    stockedProductsCount,
    // Senales de configuracion: sin estas, los pasos correspondientes no se
    // podian dar por hechos mas que a mano (ver nota en SETUP_ONBOARDING_STEPS).
    fiscalProfile,
    afipPointsCount,
    suppliersCount,
    categorizedProductsCount,
    productsWithCostCount,
    clientsCount,
    priceListsCount,
    banksCount,
    storeReadiness,
    storeOrdersCount,
    linkedPaymentMethodsCount,
  ] = await Promise.all([
    safeCount('products', organizationId),
    safeCount('sales', organizationId),
    safeCount('movimientos_caja', organizationId),
    countOrgUsers(organizationId),
    safeCount('recetas', organizationId), // F5 — senal para el paso de gastronomia
    safeCount('compras', organizationId), // H3 — senal para 'Registrá tu primera compra'
    // Stock real: filas con unidades en alguna sucursal. Un producto cargado
    // no es un producto vendible.
    safeCountWhere('stock_levels', organizationId, (q) => q.gt('quantity', 0)),
    fetchOrgFiscalProfile(organizationId),
    safeCountWhere('puntos_venta_afip', organizationId, (q) => q.eq('is_active', true)),
    safeCount('proveedores', organizationId),
    // El preset crea categorias vacias: lo que muestra trabajo real es que los
    // productos esten clasificados.
    safeCountWhere('products', organizationId, (q) => q.not('category_id', 'is', null)),
    safeCountWhere('products', organizationId, (q) => q.gt('cost_price', 0)),
    // Etapa 3. El alta deja sembrado un cliente (Consumidor Final) y una lista
    // de precios base, asi que la senal es "hay mas que los del preset".
    safeCount('clients', organizationId),
    safeCount('price_lists', organizationId),
    safeCount('banks', organizationId),
    fetchStoreReadiness(organizationId),
    safeCount('store_orders', organizationId),
    // El preset deja los 5 medios con comision pero sin banco: vincular la
    // cuenta de acreditacion es trabajo real del usuario.
    safeCountWhere('payment_methods', organizationId, (q) => q.not('bank_id', 'is', null)),
  ]);

  return {
    productsCount,
    salesCount,
    cashMovementsCount,
    usersCount,
    recipesCount,
    purchasesCount,
    stockedProductsCount,
    hasFiscalProfile: fiscalProfile?.hasFiscalProfile === true,
    afipPointsCount,
    suppliersCount,
    categorizedProductsCount,
    productsWithCostCount,
    clientsCount,
    priceListsCount,
    banksCount,
    storeContactReady: storeReadiness?.storeContactReady === true,
    storeOrdersCount,
    linkedPaymentMethodsCount,
  };
}

export async function fetchSetupOnboardingState(organizationId) {
  if (!organizationId) return normalizeSetupOnboardingState(null);

  const { data, error } = await supabase
    .from('organizations')
    .select('setup_onboarding_status')
    .eq('id', organizationId)
    .single();

  if (error) return normalizeSetupOnboardingState(null);
  return normalizeSetupOnboardingState(data?.setup_onboarding_status);
}

async function persistSetupOnboardingState(organizationId, nextState) {
  const normalized = { ...normalizeSetupOnboardingState(nextState), updatedAt: new Date().toISOString() };

  const { error } = await supabase
    .from('organizations')
    .update({ setup_onboarding_status: normalized })
    .eq('id', organizationId);

  if (error) throw error;
  return normalized;
}

export async function completeSetupOnboardingStep({ organizationId, stepId, currentState }) {
  if (!organizationId) throw new Error('Falta organizationId para guardar el paso de configuracion.');
  if (!STEP_LOOKUP.has(stepId)) throw new Error(`Paso de configuracion desconocido: ${stepId}`);

  const base = normalizeSetupOnboardingState(currentState);
  const next = {
    ...base,
    manual: { ...base.manual, [stepId]: SETUP_ONBOARDING_STATUS_COMPLETED },
  };
  return persistSetupOnboardingState(organizationId, next);
}

/**
 * Terminar la guia de un paso lo da por hecho. Es lo que el usuario espera:
 * cada paso se completa haciendo SU guia, no de rebote por un dato que dejo
 * otra (cargar un producto con costo no es "tener el catalogo costeado").
 */
export async function completeSetupStepForTour({ organizationId, tourId }) {
  const stepId = getSetupStepIdForTour(tourId);
  if (!organizationId || !stepId) return null;
  const currentState = await fetchSetupOnboardingState(organizationId);
  return completeSetupOnboardingStep({ organizationId, stepId, currentState });
}

export async function dismissSetupOnboarding({ organizationId, currentState }) {
  if (!organizationId) throw new Error('Falta organizationId para descartar el panel de configuracion.');
  const base = normalizeSetupOnboardingState(currentState);
  return persistSetupOnboardingState(organizationId, { ...base, dismissed: true });
}

export async function resetSetupOnboarding({ organizationId }) {
  if (!organizationId) throw new Error('Falta organizationId para reiniciar el onboarding de configuracion.');
  return persistSetupOnboardingState(organizationId, { version: 1, dismissed: false, manual: {}, updatedAt: null });
}
